import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc } from 'firebase/firestore';
import { Header } from '@/components/Header';
import { AdBanner } from '@/components/AdBanner';
import { Footer } from '@/components/Footer';
import { useUserPrefs } from '@/context/UserPrefsContext';
import { uiText } from '@/i18n/locales';
import { categoryLabels } from '@/i18n/categoryLabels';
import { Button, Spinner } from '@fluentui/react-components';
import type { NewsComment, LangKey } from '@/types';
import { getCount, hitAndGetCount } from '@/services/visitCounter';

export interface PostWithComments {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  user?: string;
  comments: NewsComment[];
  
}

export const CityCategoryPage: React.FC = () => {
  const { routineKey } = useParams<{ routineKey: string }>();
  const { prefs } = useUserPrefs();
  const t = uiText[prefs.language] ?? uiText.en;
  const lang = prefs.language as LangKey;
  const headerRef = useRef<{ openLogin: () => void }>(null);

  const [posts, setPosts] = useState<PostWithComments[]>([]);
  const [loading, setLoading] = useState(true);
  const [visitCount, setVisitCount] = useState<number | null>(null);

  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostDesc, setNewPostDesc] = useState('');
  const [newPostImageUrl, setNewPostImageUrl] = useState('');

  // comment input states keyed by postId or commentId for replies
  const [newCommentTitle, setNewCommentTitle] = useState<Record<string, string>>({});
  const [newCommentText, setNewCommentText] = useState<Record<string, string>>({});
  const [newCommentImageUrl, setNewCommentImageUrl] = useState<Record<string, string>>({});
  const [replyingTo, setReplyingTo] = useState<Record<string, string | null>>({}); // postId -> parent comment id
  const isLoggedIn = Boolean(prefs.userId);
  const label =
    categoryLabels[lang]?.[routineKey as keyof typeof categoryLabels[LangKey]] ??
    categoryLabels.en[routineKey as keyof typeof categoryLabels.en] ??
    routineKey;
const handleLoginOpen = () => {
  headerRef.current?.openLogin();
};

  const db = getFirestore();
  const postsPath = `CityRoutinePosts/${prefs.country}/${prefs.state}/${prefs.city}/${prefs.language}/${routineKey}/posts`;

  /** ---------- Visit Counter ---------- */
  useEffect(() => {
    if (!routineKey) return;

    const sessionKey = `visited_${routineKey}`;
    if (sessionStorage.getItem(sessionKey)) {
      getCount(routineKey).then(setVisitCount);
      return;
    }

    (async () => {
      const count = await hitAndGetCount(routineKey);
      setVisitCount(count);
      sessionStorage.setItem(sessionKey, '1');
    })();
  }, [routineKey]);

  /** ---------- Fetch posts & comments ---------- */
  useEffect(() => {
    if (!prefs.country || !prefs.state || !prefs.city || !routineKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const postsCol = collection(db, postsPath);
    const postsQuery = query(postsCol, orderBy('timestamp', 'desc'));

    const unsubscribePosts = onSnapshot(postsQuery, snap => {
      const allPosts: PostWithComments[] = snap.docs.map(doc => {
        const data = doc.data() as any;
        return {
          id: doc.id,
          title: data.title ?? '',
          description: data.description ?? '',
          imageUrl: data.imageUrl ?? '',
          user: data.user ?? 'Anonymous',
          comments: [],
        };
      });

      snap.docs.forEach(postDoc => {
        const commentCol = collection(db, `${postsPath}/${postDoc.id}/comments`);
        const commentQuery = query(commentCol, orderBy('timestamp', 'asc'));

        onSnapshot(commentQuery, csnap => {
          setPosts(prev =>
            prev.map(p => {
              if (p.id === postDoc.id) {
                const comments: NewsComment[] = csnap.docs.map(cdoc => {
                  const c = cdoc.data() as any;
                  return {
                    id: cdoc.id,
                    newsId: c.newsId ?? postDoc.id,
                    parentId: c.parentId ?? null,
                    user: c.user ?? 'Anonymous',
                    title: c.title ?? '',
                    description: c.description ?? '',
                    timestamp: c.timestamp ?? Date.now(),
                    city: c.city,
                    state: c.state,
                    country: c.country,
                    language: c.language,
                    routineKey: c.routineKey ?? null,
                    imageUrl: c.imageUrl,
                    children: [],
                  };
                });

                // Build nested comments
                const commentMap: Record<string, NewsComment> = {};
                comments.forEach(cm => (commentMap[cm.id] = cm));
                const rootComments: NewsComment[] = [];
                comments.forEach(cm => {
                  if (cm.parentId && commentMap[cm.parentId]) {
                    commentMap[cm.parentId].children = commentMap[cm.parentId].children ?? [];
                    commentMap[cm.parentId].children!.push(cm);
                  } else {
                    rootComments.push(cm);
                  }
                });

                return { ...p, comments: rootComments };
              }
              return p;
            })
          );
        });
      });

      setPosts(allPosts);
      setLoading(false);
    });

    return () => unsubscribePosts();
  }, [prefs.country, prefs.state, prefs.city, prefs.language, routineKey]);

  /** ---------- Add new post ---------- */
  const addPost = async () => {
    if (!prefs.userName) {
    headerRef.current?.openLogin();
    return;
  }
    if (!newPostTitle.trim() ) return;

    const postsCol = collection(db, postsPath);
    await addDoc(postsCol, {
      title: newPostTitle,
      description: newPostDesc,
      imageUrl: newPostImageUrl || '',
      city: prefs.city,
      state: prefs.state,
      country: prefs.country,
      language: prefs.language,
      routineKey,
      timestamp: Date.now(),
      user: prefs.userName,
    });

    setNewPostTitle('');
    setNewPostDesc('');
    setNewPostImageUrl('');
  };

  /** ---------- Add comment or reply ---------- */
  const addComment = async (postId: string, parentId: string | null = null) => {
    const text = newCommentText[postId]?.trim();
    const title = newCommentTitle[postId]?.trim() || '';
      if (!prefs.userName) {
    headerRef.current?.openLogin();
    return;
  }
    if (!text ) return;

    const commentsCol = collection(db, `${postsPath}/${postId}/comments`);
    await addDoc(commentsCol, {
      title,
      description: text,
      imageUrl: newCommentImageUrl[postId] || '',
      newsId: postId,
      parentId,
      city: prefs.city,
      state: prefs.state,
      country: prefs.country,
      language: prefs.language,
      routineKey,
      timestamp: Date.now(),
      user: prefs.userName,
    });

    // Reset input fields
    setNewCommentText(prev => ({ ...prev, [postId]: '' }));
    setNewCommentTitle(prev => ({ ...prev, [postId]: '' }));
    setNewCommentImageUrl(prev => ({ ...prev, [postId]: '' }));
    setReplyingTo(prev => ({ ...prev, [postId]: null }));
  };

  /** ---------- Recursive comment render ---------- */
  const renderComments = (postId: string, comments: NewsComment[], level = 0) => {
    return comments.map(comment => (
      <div key={comment.id} style={{ marginLeft: level * 16, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Comment one-line display */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong>{comment.user}</strong>
          {comment.title && <span>{comment.title}</span>}
          <span>{comment.description}</span>
          {comment.imageUrl && <span>[Img: {comment.imageUrl}]</span>}
          {isLoggedIn && (<button style={{ fontSize: 10 }} onClick={() => setReplyingTo(prev => ({ ...prev, [postId]: comment.id }))}>
            Reply
          </button>)}
        </div>

        {/* Reply input box if replying to this comment */}
        {replyingTo[postId] === comment.id && (
          <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
            <input
              placeholder="Reply title"
              value={newCommentTitle[postId] || ''}
              onChange={e => setNewCommentTitle(prev => ({ ...prev, [postId]: e.target.value }))}
              style={{ flex: 1, padding: 2 }}
            />
            <input
              placeholder="Reply text..."
              value={newCommentText[postId] || ''}
              onChange={e => setNewCommentText(prev => ({ ...prev, [postId]: e.target.value }))}
              style={{ flex: 2, padding: 2 }}
            />
            <input
              placeholder="Image URL"
              value={newCommentImageUrl[postId] || ''}
              onChange={e => setNewCommentImageUrl(prev => ({ ...prev, [postId]: e.target.value }))}
              style={{ flex: 1, padding: 2 }}
            />
            {isLoggedIn && ( <button onClick={() => addComment(postId, comment.id)}>Send</button>)}
          </div>
        )}

        {/* Render nested replies recursively */}
        {comment.children && comment.children.length > 0 && renderComments(postId, comment.children, level + 1)}
      </div>
    ));
  };

  /** ---------- Render ---------- */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
 <Header ref={headerRef} />  

        {/* <AdBanner adSlot="1121804425" /> */}
      <div style={{ padding: 12, maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2>{prefs.city} · {label}</h2>
    <div>
  {/* Show ad once at the top if enough items */}
  {posts.length > 3 && (
    <div style={{ marginBottom: '16px' }}>
      <AdBanner adSlot="1121804425" />
    </div>
  )}
        {/* New post */}
        {isLoggedIn ? (<div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          <input
            placeholder="Title"
            value={newPostTitle}
            onChange={e => setNewPostTitle(e.target.value)}
            style={{ flex: 2, padding: 4 }}
          />
          <input
            placeholder="Description"
            value={newPostDesc}
            onChange={e => setNewPostDesc(e.target.value)}
            style={{ flex: 3, padding: 4 }}
          />
          <input
            placeholder="Image URL"
            value={newPostImageUrl}
            onChange={e => setNewPostImageUrl(e.target.value)}
            style={{ flex: 2, padding: 4 }}
          />
          <button onClick={addPost}>Add</button>
        </div>) :
        (
         <Button size="small"  onClick={handleLoginOpen}>Login / Sign up</Button>
        )}

        {loading ? (
          <Spinner label={t.loading ?? 'Loading...'} />
        ) : posts.length ? (
          posts.map(post => (
            <div key={post.id} style={{ border: '1px solid #ccc', padding: 6, borderRadius: 6 }}>
              {/* Post one-line */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <strong>{post.user} - </strong>
                <strong>{post.title}</strong>
                <span>{post.description}</span>
                {post.imageUrl && <span>[Img: {post.imageUrl}]</span>}
              </div>

              {/* Render all comments recursively */}
              {renderComments(post.id, post.comments)}

              {/* Root comment input */}
              {isLoggedIn && ( <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                <input
                  placeholder="Comment title"
                  value={newCommentTitle[post.id] || ''}
                  onChange={e => setNewCommentTitle(prev => ({ ...prev, [post.id]: e.target.value }))}
                  style={{ flex: 1, padding: 2 }}
                />
                <input
                  placeholder="Comment..."
                  value={newCommentText[post.id] || ''}
                  onChange={e => setNewCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                  style={{ flex: 2, padding: 2 }}
                />
                <input
                  placeholder="Image URL"
                  value={newCommentImageUrl[post.id] || ''}
                  onChange={e => setNewCommentImageUrl(prev => ({ ...prev, [post.id]: e.target.value }))}
                  style={{ flex: 1, padding: 2 }}
                />
                <button onClick={() => addComment(post.id)}>Send</button>
              </div>)}
            </div>
          ))
        ) : (
          <p>{t.noNews ?? 'No posts yet.'}</p>
        )}

      </div></div>

      <Footer visitCount={visitCount} />
    </div>
  );
};
